import React, { useState, useRef, useEffect, useCallback } from 'react';
import { TextField, TextFieldProps } from '@mui/material';

interface DebouncedTextFieldProps extends Omit<TextFieldProps, 'onChange'> {
    onChange: (value: string) => void;
    debounceMs?: number;
    immediateValue?: boolean; // If true, updates the input value immediately, otherwise debounces both input and onChange
}

const DebouncedTextField: React.FC<DebouncedTextFieldProps> = ({
    onChange,
    debounceMs = 300,
    immediateValue = true,
    value: externalValue,
    ...textFieldProps
}) => {
    const [internalValue, setInternalValue] = useState(externalValue || '');
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Update internal value when external value changes
    useEffect(() => {
        if (externalValue !== undefined && externalValue !== null) {
            setInternalValue(externalValue as string);
        }
    }, [externalValue]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;

        // Clear any existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // Update internal value immediately for responsive UI
        setInternalValue(newValue);

        // Debounce the onChange callback
        timeoutRef.current = setTimeout(() => {
            onChange(newValue);
        }, debounceMs);
    }, [onChange, debounceMs]);

    const handleBlur = useCallback(() => {
        // Immediately call onChange when user leaves the field
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        onChange(internalValue as string);
    }, [onChange, internalValue]);

    return (
        <TextField
            {...textFieldProps}
            value={immediateValue ? internalValue : externalValue}
            onChange={handleChange}
            onBlur={handleBlur}
        />
    );
};

export default DebouncedTextField; 